import { useCallback, useEffect, useState } from "react";
import styled from "@emotion/styled";
import { theme } from "../lib/theme";
import { breakpoints, media } from "../lib/breakpoints";

export interface ChartOption<K extends string> {
  key: K;
  label: string;
}

/**
 * Tracks the md breakpoint in JS. Matched to `media.md` from the same constants so the gate and
 * the stylesheet can never disagree about what "narrow" means.
 */
export function useIsNarrow(): boolean {
  const query = `(max-width: ${breakpoints.md}px)`;
  const [isNarrow, setIsNarrow] = useState(
    () => typeof window.matchMedia === "function" && window.matchMedia(query).matches,
  );
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia(query);
    const onChange = (e: MediaQueryListEvent) => setIsNarrow(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [query]);
  return isNarrow;
}

/**
 * Selection state for the mobile chart picker: which metric a phone shows, and the predicate the
 * page gates each chart on. Above the breakpoint `showChart` is true for everything, so the same
 * markup serves both layouts.
 */
export function useChartPicker<K extends string>(initial: K) {
  const isNarrow = useIsNarrow();
  const [selected, setSelected] = useState<K>(initial);
  const showChart = useCallback((key: K) => !isNarrow || selected === key, [isNarrow, selected]);
  return { selected, setSelected, showChart };
}

/**
 * Mobile chart picker. Several stacked Recharts trees is a lot of DOM for a screen that can only
 * show one of them, and scrolling past charts to reach the one you want is worse still — so below
 * the md breakpoint the analytics pages render the selected metric only. Hidden above it, where
 * every chart is on screen at once and the picker would have nothing to do.
 */
export function ChartPicker<K extends string>({
  charts,
  value,
  onChange,
}: {
  charts: readonly ChartOption<K>[];
  value: K;
  onChange: (key: K) => void;
}) {
  return (
    <PickerRow>
      {charts.map((c) => (
        <PickerButton key={c.key} active={value === c.key} onClick={() => onChange(c.key)}>
          {c.label}
        </PickerButton>
      ))}
    </PickerRow>
  );
}

const PickerRow = styled.div`
  display: none;

  ${media.md} {
    display: flex;
    gap: ${theme.spacing.xs};
    margin-bottom: ${theme.spacing.sm};
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
`;

const PickerButton = styled.button<{ active: boolean }>`
  flex: 1 0 auto;
  background: ${(p) => (p.active ? theme.colors.bgHover : theme.colors.bgCard)};
  border: 1px solid ${(p) => (p.active ? theme.colors.primary : theme.colors.border)};
  color: ${(p) => (p.active ? theme.colors.text : theme.colors.textMuted)};
  border-radius: ${theme.radius.sm};
  padding: 5px 10px;
  font-size: ${theme.fontSize.xs};
  white-space: nowrap;
  cursor: pointer;
`;
